import { useMediaQuery } from '@abyss/web/hooks/useMediaQuery';
import { Flex } from '@abyss/web/ui/Flex';
import { I18nProvider } from '@abyss/web/ui/I18nProvider/I18nProvider';
import { Pagination, V2Pagination } from '@abyss/web/ui/Pagination';
import { Skeleton } from '@abyss/web/ui/Skeleton';
import { CompareProvider } from '#/models/Provider';
import { useTranslation } from 'react-i18next';

import { ConstantsLagoon } from '../../../../common/ConstantsLagoon';
import { mobileOnly } from '../../../../common/ConstantsStyles';
import { useFeatureFlag } from '../../../../hooks/useFeatureFlag';
import { DownloadResultsButton } from '../ViewAll/DownloadResultsButton';
import { PaginationControl, PagingationWrapStyled } from './MapView.styled';
import {
  StyledPageSize,
  StyledPageSizeV2,
  styledPaginationContainer,
} from './MapViewTopPagination.styled';
import { FilterProviderResultsPrintViewProps } from './Print/FilterProviderResultsPrintView';

const GroupedPinPagination = (
  topPaginationRef,
  isLoading,
  enableMapViewHideOption,
  paginationProps,
  additionalPaginationProps,
  smallScreen
) => {
  const { t } = useTranslation();
  return (
    <PagingationWrapStyled
      cssProps={{ isGroupedLocationDrawer: true }}
      ref={topPaginationRef}
    >
      <PaginationControl
        className="hide-pagination"
        css={{
          paddingBottom: '4px',
          paddingTop: smallScreen ? '16px' : '24px',
          flexDirection: 'column',
          gap: '10px',
          alignItems: 'center',
          '@screen < $md': {
            display: 'flex',
          },
        }}
        data-testid="pagination-results-container"
      >
        {isLoading ? (
          <Skeleton height={60} variant="rounded" width={540} />
        ) : (
          <>
            {enableMapViewHideOption ? (
              <I18nProvider
                translations={{
                  Pagination: {
                    previous: t('PAGINATION.PREVIOUS'),
                    next: t('PAGINATION.NEXT'),
                  },
                }}
              >
                <V2Pagination
                  {...paginationProps}
                  {...additionalPaginationProps}
                />
              </I18nProvider>
            ) : (
              <Pagination {...paginationProps} {...additionalPaginationProps} />
            )}
          </>
        )}
      </PaginationControl>
    </PagingationWrapStyled>
  );
};

type Props = {
  isLoading: boolean | undefined;
  pageSize: number;
  topPaginationRef: any;
  pageSizeOptions: any;
  setPageSizeFunc?: any;
  previousPageTop: any;
  paginationProps: any;
  gotoPages?: any;
  enableMapViewEnhancements: boolean;
  nextPageTop: any;
  downloadButtonProps?: DownloadButtonProps;
  expandedProviderListView?: boolean;
  enableMapViewHideOption?: boolean;
  isGroupedLocationDrawer?: boolean;
};

type DownloadButtonProps = {
  text: string;
  view: String;
  setOpenBoxContents: (a: boolean) => void;
  setOpenShare: (a: boolean) => void;
  setCompareShareToggle: (a: boolean) => void;
  setOpenCompare: (a: boolean) => void;
  setSelectedCheckbox: (a: { checked: {} }) => void;
  setSelectedItems: (a: CompareProvider[]) => void;
  isClicked: boolean;
  setIsClicked: React.Dispatch<React.SetStateAction<boolean>>;
  enableMapViewEnhancements: boolean;
  pageSize?: number;
  printPage?: number;
  results?: any[];
  filterProviderResultsPrintViewProps?: FilterProviderResultsPrintViewProps;
};
const downloadButtonDefaults = {
  text: '',
  view: '',
  setOpenBoxContents: () => {},
  setOpenShare: () => {},
  setOpenCompare: () => {},
  setSelectedCheckbox: () => {},
  setSelectedItems: () => {},
  isClicked: false,
  setIsClicked: () => {},
  enableMapViewEnhancements: true,
};

export const MapViewTopPagination = ({
  isLoading,
  pageSize,
  topPaginationRef,
  pageSizeOptions,
  setPageSizeFunc,
  previousPageTop,
  paginationProps,
  gotoPages,
  nextPageTop,
  downloadButtonProps,
  expandedProviderListView = false,
  enableMapViewHideOption = false,
  isGroupedLocationDrawer = false,
}: Props) => {
  const { t } = useTranslation();
  const [enableMapViewLargeResults] = useFeatureFlag([
    ConstantsLagoon.FEATURE_FLAGS.ENABLE_MAP_VIEW_LARGE_RESULTS,
  ]);
  const smallScreen = useMediaQuery(mobileOnly);
  const pageStyleProps = {
    cssProps: { isHidePageOptions: enableMapViewLargeResults },
    customPerPageLabel: t('PAGINATION.PER_PAGE'),
    customResultsLabel: t('PAGINATION.RESULTS_LABEL'),
    'data-testid': 'pagination-page-size',
    pageSize,
    pageSizeOptions,
    setPageSize: setPageSizeFunc,
  };

  const additionalPaginationProps = {
    css: {
      'abyss-pagination-abbreviated-container': {
        color: 'var(--abyss-psx-parcel-colors-gray8)',
        fontWeight: 400,
      },
    },
    customNextLabel: t('PAGINATION.NEXT'),
    customPreviousLabel: t('PAGINATION.PREVIOUS'),
    'data-testid': 'pagination-results',
    nextPage: nextPageTop,
    previousPage: previousPageTop,
    goToPage: gotoPages,
    variant:
      isGroupedLocationDrawer && smallScreen
        ? 'minimal'
        : expandedProviderListView || isGroupedLocationDrawer
        ? 'extended'
        : 'minimal',
  };

  return isGroupedLocationDrawer ? (
    GroupedPinPagination(
      topPaginationRef,
      isLoading,
      enableMapViewHideOption,
      paginationProps,
      additionalPaginationProps,
      smallScreen
    )
  ) : (
    <PagingationWrapStyled
      cssProps={{ isGroupedLocationDrawer, enableMapViewHideOption }}
      ref={topPaginationRef}
    >
      <PaginationControl
        className="hide-pagination"
        css={styledPaginationContainer(expandedProviderListView)}
        data-testid="pagination-results-container"
      >
        {!enableMapViewHideOption || !expandedProviderListView ? (
          <StyledPageSize {...pageStyleProps} />
        ) : null}
        {isLoading ? (
          <Skeleton
            height={expandedProviderListView ? 60 : 40}
            variant="rounded"
            width={expandedProviderListView ? 540 : 150}
          />
        ) : (
          <>
            {enableMapViewHideOption ? (
              <I18nProvider
                translations={{
                  Pagination: {
                    previous: t('PAGINATION.PREVIOUS'),
                    next: t('PAGINATION.NEXT'),
                  },
                }}
              >
                <V2Pagination
                  {...paginationProps}
                  {...additionalPaginationProps}
                />
              </I18nProvider>
            ) : (
              <Pagination {...paginationProps} {...additionalPaginationProps} />
            )}
          </>
        )}

        {enableMapViewHideOption && downloadButtonProps && (
          <Flex>
            <StyledPageSizeV2 {...pageStyleProps} />
            <DownloadResultsButton
              {...downloadButtonDefaults}
              {...downloadButtonProps}
            />
          </Flex>
        )}
      </PaginationControl>
    </PagingationWrapStyled>
  );
};
